import { t } from '@lingui/core/macro';
import { CarAxleType, CarRecordListForBooking, CarWheelPositionText, TireLeadReason, TireSeason } from '@navio/nsdk';
import type { StepFlowStep } from 'noddi-ui-web';
import { getCarDisplayName } from 'noddi-util';
import type { TireAxle } from './tireSizes';

export type TireSelection = 'ALL' | 'FRONT' | 'REAR' | 'CUSTOM';

// One question per step, in this order; the details step reviews and sends the request.
const REQUEST_OFFER_STEP_KEYS = ['CAR', 'SEASON', 'TIRES', 'SIZE', 'REASONS', 'DETAILS'] as const;
export type RequestOfferStepKey = (typeof REQUEST_OFFER_STEP_KEYS)[number];

/** The steps this request walks through: Tire size only when a picked car's size is unknown. */
export const getActiveStepKeys = (needsSize: boolean): RequestOfferStepKey[] =>
  REQUEST_OFFER_STEP_KEYS.filter((key) => key !== 'SIZE' || needsSize);

export const getRequestOfferSteps = (): (StepFlowStep & { key: RequestOfferStepKey })[] => [
  { key: 'CAR', label: t`Car` },
  { key: 'SEASON', label: t`Tire type` },
  { key: 'TIRES', label: t`Tires` },
  { key: 'SIZE', label: t`Tire size` },
  { key: 'REASONS', label: t`Reason` },
  { key: 'DETAILS', label: t`Details` }
];

// A customer car has these four; inner/middle positions only exist on trucks and buses.
export const CAR_WHEEL_POSITIONS = [
  CarWheelPositionText.FRONT_LEFT,
  CarWheelPositionText.FRONT_RIGHT,
  CarWheelPositionText.REAR_LEFT,
  CarWheelPositionText.REAR_RIGHT
] as const;

export type PassengerCarWheelPosition = (typeof CAR_WHEEL_POSITIONS)[number];

export const POSITIONS_BY_TIRE_SELECTION: Record<
  Exclude<TireSelection, 'CUSTOM'>,
  readonly PassengerCarWheelPosition[]
> = {
  ALL: CAR_WHEEL_POSITIONS,
  FRONT: [CarWheelPositionText.FRONT_LEFT, CarWheelPositionText.FRONT_RIGHT],
  REAR: [CarWheelPositionText.REAR_LEFT, CarWheelPositionText.REAR_RIGHT]
};

// All season is not offered to customers; the backend still accepts it from other apps.
export const getSeasonOptions = (): { value: TireSeason; label: string }[] => [
  { value: TireSeason.SUMMER, label: t`Summer tires` },
  { value: TireSeason.WINTER, label: t`Winter tires` }
];

// Asked with winter tires only; offers then hold only tires with the same answer.
export const getStudsOptions = (): { value: boolean; label: string }[] => [
  { value: true, label: t`Studded tires` },
  { value: false, label: t`Non-studded tires` }
];

export const getTireSelectionOptions = (): { value: TireSelection; label: string }[] => [
  { value: 'ALL', label: t`All 4 tires` },
  { value: 'FRONT', label: t`Front pair` },
  { value: 'REAR', label: t`Rear pair` },
  { value: 'CUSTOM', label: t`Choose tires` }
];

export const getWheelPositionLabels = (): Record<PassengerCarWheelPosition, string> => ({
  [CarWheelPositionText.FRONT_LEFT]: t`Left front`,
  [CarWheelPositionText.FRONT_RIGHT]: t`Right front`,
  [CarWheelPositionText.REAR_LEFT]: t`Left rear`,
  [CarWheelPositionText.REAR_RIGHT]: t`Right rear`
});

// Customer-facing subset of the reasons staff can record; cracks and uneven wear are for a mechanic to judge.
export const getReasonOptions = (): { value: TireLeadReason; label: string }[] => [
  { value: TireLeadReason.WORN_TREAD, label: t`Worn tread` },
  { value: TireLeadReason.PUNCTURE, label: t`Puncture or damage` },
  { value: TireLeadReason.SIDEWALL_DAMAGE, label: t`Sidewall damage` },
  { value: TireLeadReason.OLD_TIRES, label: t`Old tires` },
  { value: TireLeadReason.CUSTOMER_WISH, label: t`I just want new tires` },
  { value: TireLeadReason.OTHER, label: t`Other` }
];

export const getAxleLabels = (): Record<TireAxle, string> => ({
  [CarAxleType.FRONT]: t`Front tires`,
  [CarAxleType.REAR]: t`Rear tires`
});

export const toggleValue = <T>(values: T[], value: T, isSelected: boolean) =>
  isSelected ? [...values, value] : values.filter((existing) => existing !== value);

/** One lead per car and season. */
export const getLeadKey = (carId: number, season: TireSeason) => `${carId}:${season}`;

export const getLeadTitle = (car: CarRecordListForBooking, season: TireSeason) =>
  `${getCarDisplayName({ car, showYear: false })}: ${getSeasonOptions().find((option) => option.value === season)?.label ?? season}`;

/** The picked tires in words: the preset's name, or each picked position. */
export const getTiresLabel = (tireSelection: TireSelection, positions: readonly PassengerCarWheelPosition[]) => {
  if (tireSelection !== 'CUSTOM') {
    return getTireSelectionOptions().find(({ value }) => value === tireSelection)?.label ?? '';
  }
  const labels = getWheelPositionLabels();
  return positions.map((position) => labels[position]).join(', ');
};
